import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SimulationStatusEnum } from 'storage/agreements/agreements.model';
import { Index } from 'utils/consts';
import { CurrencyType } from 'utils/formatCurrency/formatCurrency.model';
import getProductParametersValues from 'pages/Dashboard/utils/getProductParametersValues';
import getSettledNotificationsData from 'pages/Dashboard/utils/getSettledNotificationsData';
import getDefaultAgreementPaymentsOptions from 'pages/Dashboard/utils/getDefaultAgreementPaymentsOptions';
import { IPaymentsOption } from 'pages/Dashboard/pages/Payment/Payment.model';
import { useContractOrAgreement } from './useContractOrAgreement';

interface PaymentNavigationState {
  paymentOptions: IPaymentsOption[];
  isOverpaymentMessage: boolean;
  totalDebtAmountInSystemCurrency: number;
  currencyType?: CurrencyType;
}

const usePaymentNavigationState = () => {
  const { t } = useTranslation();

  const { agreements } = useContractOrAgreement();

  const getPaymentNavigationState = useCallback(
    (agreementId: number): PaymentNavigationState | null => {
      const agreement = agreements?.find(
        (a) => a.generalData.agreementId === agreementId
      );

      if (!agreement) {
        return null;
      }

      const { generalData } = agreement;
      const {
        currency,
        overduePaymentsAmount,
        totalDebtAmountWithoutRemissionByCurrency,
        totalDebtAmountByCurrency,
        totalDebtAmountInSystemCurrency,
        totalDebtAmountInSystemCurrencyValue,
        waitingPaymentsAmountByCurrency
      } = generalData;

      const { productData } = generalData;
      if (!productData) {
        return null;
      }

      const [, debtAmountWithoutRemission] = Object.entries(
        totalDebtAmountWithoutRemissionByCurrency
      )[Index.zero];
      const [, totalDebtAmount] = Object.entries(totalDebtAmountByCurrency)[
        Index.zero
      ];

      const {
        overdue,
        productParameters,
        simulationStatus,
        prerequisitePaymentLeft,
        installments: installmentsPlan
      } = productData;

      const {
        nextInstallmentAmount,
        payoffDay,
        prerequisitePaymentDate,
        prerequisitePaymentAmount,
        installmentsCount,
        firstInstallmentPayment,
        installmentsAmount
      } = getProductParametersValues(productParameters);

      const isPrerequisitePayment =
        Number(prerequisitePaymentLeft) > 0 &&
        (simulationStatus ===
          SimulationStatusEnum.WAITING_TO_SATISFY_PREREQUISITES ||
          !!(
            prerequisitePaymentDate &&
            prerequisitePaymentAmount &&
            simulationStatus === SimulationStatusEnum.CONFIRMED
          ));

      const isInstallmentPlan = !isPrerequisitePayment && !!installmentsCount;

      const paymentOptions: IPaymentsOption[] = [];

      getSettledNotificationsData(
        isPrerequisitePayment,
        prerequisitePaymentLeft,
        paymentOptions,
        currency,
        t,
        isInstallmentPlan,
        installmentsPlan,
        overdue,
        overduePaymentsAmount,
        debtAmountWithoutRemission,
        totalDebtAmount,
        waitingPaymentsAmountByCurrency,
        prerequisitePaymentAmount,
        nextInstallmentAmount,
        firstInstallmentPayment,
        prerequisitePaymentDate,
        payoffDay,
        installmentsAmount
      );

      paymentOptions.push(...getDefaultAgreementPaymentsOptions(agreement));

      return {
        paymentOptions,
        isOverpaymentMessage: isInstallmentPlan,
        totalDebtAmountInSystemCurrency,
        currencyType: totalDebtAmountInSystemCurrencyValue
      };
    },
    [agreements, t]
  );

  return getPaymentNavigationState;
};

export default usePaymentNavigationState;
